import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Trophy, Gift, Heart, Crown, Medal, Loader2 } from "lucide-react";

const RANK_COLORS = {
  1: "#FFD700", // Gold
  2: "#C0C0C0", // Silver
  3: "#CD7F32", // Bronze
};

const RANK_EMOJIS = {
  1: "👑",
  2: "🥈",
  3: "🥉",
};

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("popular");
  const [popularData, setPopularData] = useState(null);
  const [generousData, setGenerousData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboards = useCallback(async () => {
    try {
      const [popularRes, generousRes] = await Promise.all([
        api.getPopularLeaderboard(),
        api.getGenerousLeaderboard(),
      ]);
      setPopularData(popularRes.data);
      setGenerousData(generousRes.data);
    } catch (error) {
      console.error("Failed to fetch leaderboards:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboards();
  }, [fetchLeaderboards]);

  const currentData = activeTab === "popular" ? popularData : generousData;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="leaderboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20" data-testid="leaderboard-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition"
            data-testid="leaderboard-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-[#1C3A7A] flex items-center gap-2" data-testid="leaderboard-title">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Leaderboard
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-md mx-auto">
        {/* Tabs */}
        <div className="bg-white px-4 py-2 flex border-b">
          <button
            onClick={() => setActiveTab("popular")}
            className={`flex-1 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "popular"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-500"
            }`}
            data-testid="popular-tab"
          >
            🎁 Most Popular
          </button>
          <button
            onClick={() => setActiveTab("generous")}
            className={`flex-1 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "generous"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-500"
            }`}
            data-testid="generous-tab"
          >
            💝 Most Generous
          </button>
        </div>

        {/* Description */}
        <div className="bg-yellow-50 px-4 py-3 text-center text-sm text-gray-700">
          {activeTab === "popular"
            ? "🌟 Users who received the most gifts this week"
            : "💖 Users who sent the most gifts this week"}
        </div>

        {/* Podium - Top 3 (only show if we have 3+ users) */}
        {currentData?.leaderboard?.length >= 3 && (
          <div className="bg-white py-6 px-4 flex items-end justify-center gap-4" data-testid="podium-section">
            {/* 2nd Place */}
            <div className="flex flex-col items-center" data-testid="podium-2nd">
              <img
                src={currentData.leaderboard[1]?.user.photo || "https://via.placeholder.com/60"}
                alt={currentData.leaderboard[1]?.user.name}
                className="w-16 h-16 rounded-full border-4 border-gray-400 object-cover"
              />
              <span className="text-2xl mt-1">🥈</span>
              <p className="text-sm font-medium text-gray-800 truncate max-w-[80px]">
                {currentData.leaderboard[1]?.user.name}
              </p>
              <p className="text-xs text-gray-500">
                {currentData.leaderboard[1]?.gift_count} 🎁
              </p>
            </div>

            {/* 1st Place */}
            <div className="flex flex-col items-center -mt-6" data-testid="podium-1st">
              <span className="text-3xl mb-1">👑</span>
              <img
                src={currentData.leaderboard[0]?.user.photo || "https://via.placeholder.com/70"}
                alt={currentData.leaderboard[0]?.user.name}
                className="w-20 h-20 rounded-full border-4 border-yellow-400 object-cover"
              />
              <p className="text-base font-semibold text-[#1C3A7A] mt-2 truncate max-w-[90px]">
                {currentData.leaderboard[0]?.user.name}
              </p>
              <p className="text-sm text-gray-500">
                {currentData.leaderboard[0]?.gift_count} 🎁
              </p>
            </div>

            {/* 3rd Place */}
            <div className="flex flex-col items-center" data-testid="podium-3rd">
              <img
                src={currentData.leaderboard[2]?.user.photo || "https://via.placeholder.com/55"}
                alt={currentData.leaderboard[2]?.user.name}
                className="w-14 h-14 rounded-full border-4 border-amber-600 object-cover"
              />
              <span className="text-2xl mt-1">🥉</span>
              <p className="text-sm font-medium text-gray-800 truncate max-w-[70px]">
                {currentData.leaderboard[2]?.user.name}
              </p>
              <p className="text-xs text-gray-500">
                {currentData.leaderboard[2]?.gift_count} 🎁
              </p>
            </div>
          </div>
        )}

        {/* Simple list for when we have 1-2 users (no podium) */}
        {currentData?.leaderboard?.length > 0 && currentData?.leaderboard?.length < 3 && (
          <div className="bg-white mt-2" data-testid="leaderboard-list-simple">
            {currentData.leaderboard.map((item) => (
              <div
                key={item.user.id}
                className={`flex items-center px-4 py-4 border-b ${
                  item.is_current_user ? "bg-yellow-50" : ""
                } ${item.rank === 1 ? "bg-gradient-to-r from-yellow-50 to-white" : ""}`}
                data-testid={`leaderboard-item-${item.rank}`}
              >
                {/* Rank with medal for top 3 */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                  item.rank === 1 ? "bg-yellow-400" : item.rank === 2 ? "bg-gray-300" : item.rank === 3 ? "bg-amber-600" : "bg-gray-100"
                }`}>
                  {item.rank <= 3 ? (
                    <span className="text-xl">{item.rank === 1 ? "👑" : item.rank === 2 ? "🥈" : "🥉"}</span>
                  ) : (
                    <span className="text-sm font-bold text-gray-600">{item.rank}</span>
                  )}
                </div>

                {/* Photo */}
                <img
                  src={item.user.photo || "https://via.placeholder.com/50"}
                  alt={item.user.name}
                  className={`rounded-full object-cover mr-3 ${item.rank === 1 ? "w-14 h-14 border-2 border-yellow-400" : "w-12 h-12"}`}
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium text-gray-800 truncate ${item.rank === 1 ? "text-lg" : ""}`}>
                      {item.user.name}
                    </p>
                    {item.is_current_user && (
                      <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded">
                        YOU
                      </span>
                    )}
                  </div>
                  {activeTab === "popular" && item.top_gifts && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(item.top_gifts)
                        .slice(0, 4)
                        .map(([emoji, count]) => (
                          <span key={emoji} className="text-xs text-gray-500">
                            {emoji}x{count}
                          </span>
                        ))}
                    </div>
                  )}
                  {activeTab === "generous" && (
                    <p className="text-xs text-gray-500 mt-1">
                      {item.total_value} coins given
                    </p>
                  )}
                </div>

                {/* Count */}
                <div className="text-center">
                  <p className={`font-bold text-[#1C3A7A] ${item.rank === 1 ? "text-2xl" : "text-lg"}`}>
                    {item.gift_count}
                  </p>
                  <p className="text-xs text-gray-500">
                    {activeTab === "popular" ? "received" : "sent"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rest of list (rank 4+) when we have podium */}
        {currentData?.leaderboard?.length >= 3 && (
          <div className="bg-white mt-2" data-testid="leaderboard-list">
            {currentData?.leaderboard?.slice(3).map((item) => (
            <div
              key={item.user.id}
              className={`flex items-center px-4 py-3 border-b ${
                item.is_current_user ? "bg-yellow-50" : ""
              }`}
              data-testid={`leaderboard-item-${item.rank}`}
            >
              {/* Rank */}
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                <span className="text-sm font-bold text-gray-600">
                  {item.rank}
                </span>
              </div>

              {/* Photo */}
              <img
                src={item.user.photo || "https://via.placeholder.com/50"}
                alt={item.user.name}
                className="w-12 h-12 rounded-full object-cover mr-3"
              />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-800 truncate">
                    {item.user.name}
                  </p>
                  {item.is_current_user && (
                    <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded">
                      YOU
                    </span>
                  )}
                </div>
                {activeTab === "popular" && item.top_gifts && (
                  <div className="flex gap-1 mt-1">
                    {Object.entries(item.top_gifts)
                      .slice(0, 4)
                      .map(([emoji, count]) => (
                        <span key={emoji} className="text-xs text-gray-500">
                          {emoji}x{count}
                        </span>
                      ))}
                  </div>
                )}
                {activeTab === "generous" && (
                  <p className="text-xs text-gray-500 mt-1">
                    {item.total_value} coins given
                  </p>
                )}
              </div>

              {/* Count */}
              <div className="text-center">
                <p className="text-lg font-bold text-[#1C3A7A]">
                  {item.gift_count}
                </p>
                <p className="text-xs text-gray-500">
                  {activeTab === "popular" ? "received" : "sent"}
                </p>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Current User Status (if not on leaderboard) */}
        {activeTab === "popular" &&
          popularData?.current_user &&
          !popularData.current_user.on_leaderboard && (
            <div className="mx-4 mt-4 p-4 bg-white rounded-xl border-2 border-dashed border-orange-300" data-testid="your-rank-section">
              <h3 className="text-center font-bold text-[#1C3A7A] mb-2">
                Your Rank
              </h3>
              {popularData.current_user.rank ? (
                <div className="text-center">
                  <p className="text-3xl font-bold text-orange-500">
                    #{popularData.current_user.rank}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {popularData.current_user.gift_count} gifts received this
                    week
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Receive {10 - popularData.current_user.gift_count} more
                    gifts to earn the 🎁 Popular badge!
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    No gifts received yet this week
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Be active and get matched to receive gifts!
                  </p>
                </div>
              )}
            </div>
          )}

        {/* Empty State */}
        {(!currentData?.leaderboard || currentData.leaderboard.length === 0) && (
          <div className="text-center py-12 px-4" data-testid="leaderboard-empty">
            <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No data yet</h3>
            <p className="text-sm text-gray-500 mt-1">
              Be the first to send or receive gifts this week!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
